import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';

import { IAccountUser } from '@account-users/account/models/IAccountUser';
import { IAccount } from '@account-users/account/models/IAccount';
import { User } from '@account-users/user/models/User';
import { UserInvitation } from '@account-users/account/models/UserInvitation';
import { IToolInfo } from '@account-users/account/models/IToolInfo';
import { CustomHttpParamEncoder } from '@shared';
import { ISearchUser } from '../models/ISearchUser';
import { UserRoles } from '@account-users/account/models/UserRoles';
import { IBulkEdit } from '@account-users/account/models/LicensePermissionList';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private readonly http: HttpClient,
    @Inject('apiBaseUrl') private apiBaseUrl: string,
  ) {}

  public fetchUser(userId = '', refreshCache = true) {
    return this.http.get(
      `${this.apiBaseUrl}users/${encodeURIComponent(userId)}${
        refreshCache ? '?refreshCache=true' : ''
      }`,
    ) as Observable<User>;
  }

  public deleteBlockedByLogin({ userId }: User) {
    return this.http.delete(
      `${this.apiBaseUrl}users/${encodeURIComponent(userId)}/block`,
    );
  }

  public deleteMFAForUser({ userId }: User) {
    return this.http.delete(
      `${this.apiBaseUrl}users/${encodeURIComponent(userId)}/mfa`,
    );
  }

  public putUserRole(role: string, userId: string) {
    return this.http.put(
      `${this.apiBaseUrl}/users/${encodeURIComponent(userId)}/roles/${role}`,
      {},
    );
  }
  public deleteUserRole(role: string, userId: string) {
    return this.http.delete(
      `${this.apiBaseUrl}/users/${encodeURIComponent(userId)}/roles/${role}`,
    );
  }

  public updateAccountUserRole(
    role: string,
    userId: string,
    accountId: string,
    method: string,
  ) {
    return this.http[method](
      `${this.apiBaseUrl}accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}/roles/${role}`,
      {},
    );
  }
  public searchUsers(
    query: string = '',
    roles: string[] = [],
  ): Observable<ISearchUser[]> {
    return this.http.get<ISearchUser[]>(`${this.apiBaseUrl}users/search`, {
      params: new HttpParams({
        fromObject: {
          q: query || '',
          roles: roles || [],
        },
        encoder: new CustomHttpParamEncoder(),
      }),
    });
  }

  public searchAccountUsers(
    query: string = '',
    roles: string[] = [],
    permissions: string[] = [],
    accountCategories: string[] = [],
  ): Observable<ISearchUser[]> {
    return this.http.get<ISearchUser[]>(
      `${this.apiBaseUrl}accounts/users/search`,
      {
        params: new HttpParams({
          fromObject: {
            q: query || '',
            roles: roles || [],
            permissions: permissions || [],
            accountCategories: accountCategories || [],
          },
          encoder: new CustomHttpParamEncoder(),
        }),
      },
    );
  }

  public bulkEditAccountUsersPermissions(data: any[]): Observable<any> {
    return this.http.post<IBulkEdit[]>(
      `${this.apiBaseUrl}accounts/users/licensepermissions/remove`,
      data,
    );
  }

  public getUserDetails(accountId: string, userId: string) {
    return this.http.get(
      `${this.apiBaseUrl}v2/accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}`,
    ) as Observable<IAccountUser>;
  }
  public deleteUserFromAccount(userId: string, accountId: string) {
    return this.http.delete(
      `${this.apiBaseUrl}accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}`,
    ) as Observable<IAccount>;
  }

  public inviteUser(
    accountId: string,
    userInvitation: UserInvitation,
  ): Observable<IAccountUser> {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/users`,
      userInvitation,
    ) as Observable<IAccountUser>;
  }

  public updatePermission({ toolUpdate, accountId, userId }) {
    const { checked, toUpdate, toolId } = toolUpdate;
    const method = checked ? 'put' : 'delete';
    return this.http[method](
      `${this.apiBaseUrl}v2/accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}/licenses/${toolId}/permissions/${toUpdate}`,
      {} as any,
    ) as Observable<IToolInfo>;
  }

  public setSectionPermissions({ method, accountId, userId, sectionId }) {
    return this.http[method](
      `${this.apiBaseUrl}accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}/sections/${sectionId}`,
      {} as any,
    ) as Observable<any>;
  }

  public inviteStaffUser(
    accountId,
    toolsPermissions: IToolInfo[],
    roles: UserRoles[],
    sections: string[],
    userId: string,
    expires: string = null,
  ) {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/users/staff`,
      { toolsPermissions, roles, sections, userId, expires },
    ) as Observable<IAccountUser>;
  }

  public invitePartnerUser(
    accountId,
    userId: string,
    expires: string,
    roles: UserRoles[],
    toolsPermissions: IToolInfo[],
    sections: string[],
  ) {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/users/partner`,
      { toolsPermissions, roles, sections, userId, expires },
    ) as Observable<IAccountUser>;
  }

  public fetchAllowedAccounts(userId = '') {
    return this.http.get(
      `${this.apiBaseUrl}users/${encodeURIComponent(userId)}/accounts`,
    ) as Observable<IAccount[]>;
  }

  public fetchUserRoles() {
    return this.http.get(
      `${this.apiBaseUrl}configurations/roles`,
    ) as Observable<any>;
  }

  public blockUser(userId: string) {
    return this.http.put(
      `${this.apiBaseUrl}users/${encodeURIComponent(userId)}/block`,
      null,
    );
  }
}

// https://gw.au-stage.apsis.cloud/backoffice/v2/accounts/q4oc8zawsjpyjaa4/users/auth0%7C6666d5a720023479988d5ec3
